import applyDrilldownActionFactory from './apply-drilldown-action-factory'
import isGeographicOrRollup from 'scoreboard/datasets/is-geographic-or-rollup'
import _ from 'lodash'
import Backbone from 'backbone'
import $ from 'jquery'
import Dataset, { isEditField } from 'scoreboard/datasets/model/dataset'
import ErrorView from 'scoreboard/components/error-view'
import appDisplay from 'scoreboard/components/app-display'
import isComplete from 'scoreboard/datasets/filters/is-complete'
import unwrapModel from 'scoreboard/components/unwrap-model'
import isNoop from 'scoreboard/datasets/filters/is-noop'
import uniqueDrilldownActionId from './unique-drilldown-action-id'
import { clearFilterState }
	from 'scoreboard/dashboards/display/dataset-filters-layout'
import QueryQueue, { ALL } from './query-queue'

/**
 * Coordinates all the dataset-related widgets on a dashboard.
 */
const Maestro = function ({ data, embeddedToken, state, getPeriodId }) {
	this.data = data
	this.embeddedToken = embeddedToken
	this.widgetViews = []
	this.chartViews = []
	this.datasetReportViews = []
	this.xhrsByDatasetId = {}
	this.drilldownFiltersByViewCid = {}
	this.drawerFilters = {}
	this.initialState = state
	this.getPeriodId = getPeriodId
	this.debouncedFetchCharts = {}

	if (state && state.liveFilters) {
		// If the dashboard is restoring any initial state (like in a bookmark
		// or export), initialize drawerFilters with any that were in the saved
		// state.
		//
		// Note that we don't need to worry about non-drawerFilters in here,
		// because any widget-level filters are initialized directly on the widgets.
		_.toPairs(state.liveFilters).forEach(([datasetId, filters]) => {
			this.drawerFilters[datasetId] = _.filter(filters, f => f.drawerFilter)
		})
	}

	this.queryQueue = new QueryQueue()
	this.listenTo(this.queryQueue, 'query', this.onQueueReady.bind(this))
}

_.extend(Maestro.prototype, Backbone.Events, {
	add (widgetView) {
		widgetView.model.maestro(this)
		if (isChartView(widgetView)) {
			this.chartViews.push(widgetView)

			Array.of('drilldown', 'back').forEach(event => {
				this.listenTo(widgetView, event, drilldown => {
					this.chartDrilldown(event, widgetView, drilldown)
				})
			})

			let rollupFieldId = widgetView.model.chart.get('rollupFieldId')
			if (rollupFieldId) {
				// If there is some initial state (like on an export), any filters
				// applied to a map's rollup field should be considered drilldown
				// filters, so they are applied properly to any other widgets
				_(this.initialState?.liveFilters).values().flatten().forEach(filter => {
					if (filter.fieldId === rollupFieldId) {
						this.drilldownFiltersByViewCid[widgetView.cid] = _.concat(
							(this.drilldownFiltersByViewCid[widgetView.cid] ?? []),
							[filter])
					}
				})
			}

			return
		}

		if (isChartTableView(widgetView)) {
			return
		}

		if (isDatasetReportModel(widgetView.model)) {
			this.datasetReportViews.push(widgetView)
		}

		if (!(isDatasetWidgetModel(widgetView.model)
			|| isPotentialDatasetValueModel(widgetView.model))) {
			// Always include shape/speedometer/gauge widgets, because even if
			// they don't currently have a dataset source, they could be changed,
			// and then we would need maestro to know about them
			return
		}

		this.widgetViews.push(widgetView)

		if (!_.isEmpty(widgetView.drilldownFilters)) {
			// If the widget already has some drilldown filters (like when opening
			// a bookmark that was created after a drilldown), record them for when
			// the query is done
			this.drilldownFiltersByViewCid[widgetView.cid] = [_.last(widgetView.drilldownFilters)]
		}

		this.listenTo(widgetView, 'drilldown', (table, value) => this.drilldown(widgetView, table, value))
		this.listenTo(widgetView, 'back', (table, result) => this.back(widgetView, table, result))
		this.listenTo(widgetView, 'drilldown:value', drilldown => {
			this.drilldownValueWidget(widgetView, drilldown)
		})
	},

	'delete' (widgetModel) {
		if (isChartModel(widgetModel)) {
			_.remove(this.chartViews, v => v.model === widgetModel)
			return
		}

		if (!isDatasetWidgetModel(widgetModel)) {
			return
		}

		let widgetView = _.find(this.widgetViews, v => v.model === widgetModel)

		this.stopListening(widgetView)
		this.stopListening(widgetView.model.properties)

		_.remove(this.widgetViews, v => v === widgetView)

		if (isDatasetFilterView(widgetView)) {
			// If a filter widget was removed, it may have been limiting the data
			// in other dataset widgets and charts, so let's fetch new data
			let datasetId = widgetView.model.datasetId()
			this.query(datasetId)
		}
	},

	drilldown (widgetView, table, value, drilldownActionId, opt) {
		this._doDrilldown(widgetView, () => widgetView.datasetDrilldown(table, value, drilldownActionId), opt)
	},

	back (widgetView, table, result, drilldownActionId) {
		this._doDrilldown(widgetView, () => widgetView.datasetBack(table, result, drilldownActionId))
	},

	chartDrilldown (event, widgetView, drilldown) {
		this.lastDrilldownView = widgetView
		const datasetId = widgetView.model.datasetId()

		if (!!datasetId) {
			// #49072 trigger a corresponding drilldown on any dataset tables
			// on the dashboard. Just adding the drilldown filter isn't sufficient
			// if the table widget has a manual filter; there is logic in the
			// dataset table view that ensures the correct hierchy level is
			// displayed

			let drilldownActionId = getDrilldownActionId(drilldown.drilldownFilters)
			let oldDrilldownFilters = this.drilldownFiltersByViewCid[widgetView.cid] ?? []

			this._applyDrilldownToTableWidgets({
				event,
				drilldownSource: widgetView,
				drilldown,
				drilldownActionId
			})

			this._applyDrilldownToFilterWidgets({
				datasetId,
				drilldownFilters: drilldown.drilldownFilters,
				oldDrilldownFilters,
				treeItemLookup: treeItemId => {
					return widgetView.model.chart.data.treeItems(treeItemId)
				}
			})

			this.drilldownFiltersByViewCid[widgetView.cid] = drilldown.drilldownFilters

			this.pushFiltersToReportsForDataset(datasetId)

			// #48574 #50071 if drilling down to a leaf node in a map, we need to
			// fetch all widgets except the map (which has all the data it needs)
			this.fetchForAffectedDatasets(datasetId,
				drilldown.isZoomed() ? [ widgetView ] : null)
		} else {
			// if there is no dataset, then we are viewing placeholder data
			// the drilldown id has changed so we can just refetch the data
			widgetView.model.data.fetch({
				periodId: this.getPeriodId()
			}).then(() => {
				this.trigger('chartFetch:done')
			})
		}
	},

	_doDrilldown (widgetView, fn, opt) {
		this.lastDrilldownView = widgetView

		let { drilldownFilters, oldDrilldownFilters } =
			this._applyDrilldownToTableWidget({ widgetView, fn })

		this._applyDrilldownToCharts({
			drilldownFilters,
			oldDrilldownFilters,
			drilldownSource: opt?.drilldownSource
		})

		this._applyDrilldownToFilterWidgets({
			datasetId: widgetView.model.datasetId(),
			drilldownFilters,
			oldDrilldownFilters
		})

		this.pushFiltersToReportsForDataset(widgetView.model.datasetId())

		// #50071 if this table drilldown originally came from a chart drilldown,
		// then don't do the drilldown logic on that chart again
		let skipViews = []
		if (opt?.drilldownSource && isChartView(opt.drilldownSource)) {
			skipViews.push(opt.drilldownSource)
		}

		this.fetchForAffectedDatasets(widgetView.model.datasetId(), skipViews)
	},

	_applyDrilldownToTableWidget({ widgetView, fn }) {
		let oldDrilldownFilters = this.drilldownFiltersByViewCid[widgetView.cid]
		let drilldownFilters = fn()
		this.drilldownFiltersByViewCid[widgetView.cid] = drilldownFilters

		return { drilldownFilters, oldDrilldownFilters }
	},

	_applyDrilldownToTableWidgets({ event, drilldownSource, drilldown, drilldownActionId }) {
		let datasetId = drilldownSource.model.datasetId()

		this.widgetViews.filter(isDatasetTableView)
			.filter(tableView => {
				// Only perform the drilldown/back action on the table view
				// if the table is using the same field as the chart drilldown.
				// Any other tables will still get drilldown filters, we just
				// don't need to directly perform the action on the table view
				//
				// #60539 don't compare the dataset IDs, because there might
				// be linked datasets involved. Comparing the underlying fields
				// will take care of that

				let dataset = tableView.dataset()
				let tableField = dataset.getUnderlyingField(
					unwrapModel(tableView.table()).fieldId)
				let drilldownField = dataset.getUnderlyingField(drilldown.fieldId)

				return tableField.id == drilldownField?.id
			})
			.forEach(tableView => {
				let fn
				if (event === 'drilldown') {
					// If the chart is zoomed into specific nodes (zoomTo), then
					// pass those IDs in so the table does a drilldown to those
					// nodes. Otherwise, just use the drilldownId for the chart
					let drilldownValue = _.isEmpty(drilldown.zoomTo)
						? drilldown.drilldownId
						: drilldown.zoomTo.map(treeItem => treeItem.id)
					fn = () => tableView.datasetDrilldown(tableView.table(), drilldownValue, drilldownActionId)

				} else {
					// applyFilter.back() expects the results of the dataset table query,
					// but there isn't a query yet (since this was a chart drilldown).
					// However, applyFilter.back() only needs the parentId, so we'll just
					// spoof it
					let fakeResult = { parentId: drilldown.drilldownId }
					fn = () => tableView.datasetBack(tableView.table(), fakeResult, drilldownActionId)
				}
				this._applyDrilldownToTableWidget({
					widgetView: tableView,
					fn
				})
			})
	},

	_applyDrilldownToCharts ({ drilldownFilters, oldDrilldownFilters, drilldownSource }) {
		// Update the chart views with the filters produced by the table drilldown
		this.chartViews.forEach(v => {
			if (!isDrilldownChartView(v)) {
				return
			}

			// To figure out if the table drilldown/back action applies to this
			// chart, see if the drilldown filters apply to the chart's dataset
			let filterIsApplicable = _.some(v.model.chart.getAllDatasetIds(), datasetId => {
				return filterAppliesToDataset({
					dashboardData: v.model.data,
					// Fall back on the old filters if there aren't any new filters
					// (this happens when going Back to the root level)
					filter: _.first(drilldownFilters) ?? _.first(oldDrilldownFilters),
					subjectDatasetId: datasetId
				})
			})

			if (!filterIsApplicable) {
				return
			}

			// The chart drilldown filters should match the table
			this.drilldownFiltersByViewCid[v.cid] = _.cloneDeep(drilldownFilters)

			if (v !== drilldownSource) {
				v.applyDrilldown(drilldownFilters)
			}
		})
	},

	_applyDrilldownToFilterWidgets ({ datasetId, drilldownFilters, oldDrilldownFilters, treeItemLookup }) {
		// Note: can be null if we're going "back" to the root level
		let drilldownFilter = drilldownFilters?.[0]
		let filterFieldId = (drilldownFilter ?? oldDrilldownFilters?.[0])?.fieldId
		if (_.isNil(filterFieldId)) {
			// #56163 there might not current *or* old drilldown filters, if we
			// hit Back on a dataset table with "manual" filters (ie only apply
			// to the table widget itself). In that case, there's nothing to apply
			// to other widgets, because those filters only applied to the
			// table widget itself.
			return
		}

		this.widgetViews.filter(isDatasetFilterView)
			.filter(filterView => {
				// Only perform the drilldown/back action on the filter view
				// if the filter is using the same field as the table drilldown.
				if (filterView.model.datasetId() != datasetId) {
					return false
				}

				let dataset = filterView.dataset()
				let filterField = filterView.field()
				let drilldownField = dataset.getUnderlyingField(filterFieldId)

				return filterField.id == drilldownField?.id
			})
			.forEach(filterView => {
				this.drilldownFiltersByViewCid[filterView.cid] = drilldownFilters

				filterView.filter(drilldownFilter)
				let treeItems = _.keys(drilldownFilter?.rollupIsAnyFilters)
					.map(treeItemId => treeItemLookup?.(treeItemId))
					.filter(t => !!t)

				filterView.model.data.addDatasetTreeItems(treeItems)

				filterView.render()
			})
	},

	drilldownValueWidget (widgetView, drilldown) {
		let request = drilldown.request
		let dataset = this.datasetModel(widgetView.model.datasetId())

		// The request that came from DatasetValueWidgetView.drilldown() only
		// includes "manual" filters that were added specifically to that widget.
		// We also need to add in any "live" filters on the dashboard (filter widgets,
		// table drilldowns, drawer filters)
		let liveFilters = _.filter(this.liveFilters()[drilldown.id], filter => {
			return !isNoop(filter, dataset)
		})

		if (!_.isEmpty(liveFilters)) {
			if (!request.filters) {
				request.filters = []
			}
			adjustLiveFiltersForDrilldown(liveFilters, request)
			request.filters = request.filters.concat(liveFilters)
		}

		Backbone.trigger(Dataset.DRILLDOWN, drilldown.id, { request })
	},

	changeFilter (widgetView, filter, previousDatasetId) {
		let field = widgetView.field()
		if (isGeographicOrRollup(field.dataType)) {
			this.lastDrilldownView = widgetView

			let oldFilter = widgetView.filter()

			let applyDrilldownAction = applyDrilldownActionFactory({
				widgetView
			})
			applyDrilldownAction(filter)

			let drilldownFilters = [ filter ]
			let oldDrilldownFilters = [ oldFilter ]
			let datasetId = widgetView.model.datasetId()

			this._applyDrilldownToFilterWidgets({
				datasetId,
				drilldownFilters,
				oldDrilldownFilters
				// Don't need to pass in treeItemLookup, because we the filter widgets
				// already have it from the dialog
			})

			this._applyDrilldownToCharts({
				drilldownFilters,
				oldDrilldownFilters,
				drilldownSource: widgetView
			})

			let drilldown = getDrilldownState(filter)

			let drilldownActionId = getDrilldownActionId(drilldown.drilldownFilters)

			this._applyDrilldownToTableWidgets({
				// Don't have the info to determine drilldown vs back on a filter widget,
				// but it seems to be okay in this scenario to just always do 'drilldown'
				event: 'drilldown',
				drilldownSource: widgetView,
				drilldown,
				drilldownActionId
			})
		} else {
			// #57995 filter may have been changed from geo/rollup to
			// non-geo/rollup, meaning it's no longer a "drilldown" filter and
			// should be cleared out
			this.drilldownFiltersByViewCid[widgetView.cid] = [ ]
			widgetView.filter(filter)
		}

		// Fetch fresh data for any widgets using the dataset associated with
		// the filter
		this.fetchForAffectedDatasets(widgetView.model.datasetId())

		if (!_.isNil(previousDatasetId)
			&& previousDatasetId !== widgetView.model.datasetId()) {
			// #57983 if the filter was changed from one dataset to another,
			// we need to fetch any widgets that are using that dataset, too
			this.fetchForAffectedDatasets(previousDatasetId)
		}

		this.pushFiltersToReportsForDataset(widgetView.model.datasetId())
	},

	/**
	 * Called when filters are updated through the drawer UI. Updates any necessary
	 * widget UI and fetches new data.
	 *
	 * @param datasetId ID of the filters' dataset
	 *
	 * @param drawerFilters filters that were manually added through the drawer UI
	 *
	 * @param widgetFilters filters that came from dashboard widgets (which may
	 * have been updated through the drawer UI)
	 */
	changeFilters ({ datasetId, drawerFilters, widgetFilters }) {
		this.lastDrilldownView = null

		this.drawerFilters[datasetId] = drawerFilters

		widgetFilters.concat(drawerFilters).forEach(newFilter => {
			_.forEach(this.widgetViews, v => {
				if (isDatasetFilterView(v)) {
					let oldFilter = v.filter()
					if (oldFilter.cid === newFilter.cid) {
						this.changeFilter(v, newFilter)
					}
				} else if (isDatasetTableView(v)) {
					let currentFilters = v.filters()
					currentFilters.forEach((oldFilter, i) => {
						if (oldFilter.cid === newFilter.cid) {
							this._doDrilldown(v, () => {
								return v.datasetDirectDrilldown(newFilter)
							}, { drilldownSource: v })
						}
					})
				}
			})
		})

		this.fetchForAffectedDatasets(datasetId)
		this.pushFiltersToReportsForDataset(datasetId)
	},

	/**
	 * For dashboards in a form widget, this is called when the filter source
	 * widget's value changes. This will update the dashboard's filters and
	 * fetch new data.
	 */
	changeSourceFilter({ datasetId, filter }) {
		// The source filter is kept in the drawerFilters, so just find and
		// replace that one with the new filter
		this.drawerFilters[datasetId] = _.map(this.drawerFilters[datasetId], drawerFilter => {
			return drawerFilter.cid === filter.cid ? filter : drawerFilter
		})

		this.fetchForAffectedDatasets(datasetId)
		this.pushFiltersToReportsForDataset(datasetId)
	},

	/**
	 * Called when a filter is deleted through the drawer UI
	 */
	deleteFilter: function (datasetId, deleted) {
		this.widgetViews.concat(this.chartViews).forEach(v => {

			function matchesDeletedFilter (f) {
				if (!_.isNil(f.cid) && f.cid == deleted.cid) {
					return true
				}

				if (!_.isNil(f.drilldownActionId)
					&& f.drilldownActionId == deleted.drilldownActionId) {
					return true
				}

				return false
			}

			if (isDatasetTableView(v)) {
				// Clear out any drilldown filters on the table widget that match
				// the deleted filter
				let oldFilters = v.filters()
				let updatedFilters = oldFilters.filter(f => !matchesDeletedFilter(f))
				if (oldFilters.length !== updatedFilters.length) {
					v.filters(updatedFilters)

					this._doDrilldown(v, () => {
						return v.datasetDirectDrilldown(null)
					}, { drilldownSource: v })
				}
			} else if (isDatasetFilterView(v)) {
				// #56298 if the filter widget matches the deleted filter, clear
				// out its state (so the filter widget shows up as empty)
				let f = v.filter()
				if (matchesDeletedFilter(f)) {
					clearFilterState(f, this.datasetModel(datasetId))
					v.filter(f)
				}
			} else if (isDrilldownChartView(v)) {
				// If the chart has a drilldown filter matching the deleted filter,
				// clear out its drilldown state
				let chartFilters = v.model.chart.data.currentDrilldown()?.drilldownFilters
				if (_.find(chartFilters, f => matchesDeletedFilter(f))) {
					v.clearDrilldown()
				}
			}

			if (_.find(this.drilldownFiltersByViewCid[v.cid], f => matchesDeletedFilter(f))) {
				this.drilldownFiltersByViewCid[v.cid] = []
			}
		})

		_.remove(this.drawerFilters[datasetId], f => f.cid === deleted.cid)

		this.fetchForAffectedDatasets(datasetId)
		this.pushFiltersToReportsForDataset(datasetId)
	},

	fetchForAffectedDatasets (datasetId, skipViews) {
		let dataset = this.datasetModel(datasetId)
		if (dataset == null) {
			// #60254 if the dataset isn't one of the "main" datasets in this.data,
			// it might be a linked dataset in one of the "main" datasets.
			// If so, use one of the datasets that links to it, since that
			// dataset (and any linked to it) could be affected by changes
			// related to this dataset
			let sourceDataset = _.find(this.data.datasets(), ds => {
				return _.find(ds.linkedDatasets, l => l.id == datasetId)
			})
			if (sourceDataset) {
				dataset = this.datasetModel(sourceDataset.id)
			}
		}

		let datasetIdsToFetch = getDatasetIdsToFetch({
			dataset,
			widgetViews: this.widgetViews
		})

		datasetIdsToFetch.forEach(datasetId => {
			this.query(datasetId, null, skipViews)
			this.fetchCharts(datasetId, skipViews)
		})
	},

	pushFiltersToReportsForDataset(datasetId) {
		this.datasetReportViews
			.filter(it =>
				it.model.report.get('dataset').get('id') === parseInt(datasetId))
			.forEach(view => {
				// #43927 Not a great abstraction, but base-report-results.js clones
				// off its own copy of the report, and if we don't interact with that
				// copy, the reload event will not go to the right place and nothing
				// will happen visually.
				const report = view.results.viewModel
				view.updateLiveFilters(report)
				report.reload()
			})
	},

	/**
	 * Fetches all charts for the given dataset.
	 *
	 * @param {Integer} {datasetId} ID of the dataset. If null, all charts that
	 * do not have a dataset will be fetched
	 *
	 * @param {Array} [skipViews] Array of views to skip, even if they have
	 * the given dataset ID. If null, no views will be skipped
	 */
	fetchCharts (datasetId, skipViews) {
		this.chartViews
			.filter(v => {
				return isChartView(v) && !_.includes(skipViews, v)
			})
			.filter(v => {
				return _.isNil(datasetId)
					? !isDatasetChartView(v)
					: this.chartUsesDataset(v, datasetId)
			})
			.forEach(v => this.fetchChart(v))
	},

	fetchChart (v) {
		let fetch = this.debouncedFetchCharts[v.cid]
		if (!fetch) {
			// Some charts have multiple datasetIds, and we don't want
			// to double-fetch, so just use a debounced function
			fetch = _.debounce(() => this._doFetchChart(v), 0)
			this.debouncedFetchCharts[v.cid] = fetch
		}
		fetch()
	},

	_doFetchChart (v) {
		v.toggleLoading(true)
		v.model.fetchChart({
			periodId: this.getPeriodId()
		}).then(() => {
			if (this.removed) {
				// #60111 left the view before XHR finished; no need to render
				return
			}

			v.renderContent()
			this.addTreeItemsToFilterWidgets(
				_.values(v.model.data.datasetTreeItems()))
			this.trigger('chartFetch:done')
		})
	},

	liveFilters () {
		let filtersByDatasetId = {}

		function addFilters (datasetId, filters) {
			if (_.isEmpty(filters)) {
				return
			}

			let existingFilters = filtersByDatasetId[datasetId] || []

			// Since we're combining filters from multiple widgets, there might
			// be duplicates if they were generated by a drilldown. So only use
			// one filter with a given drilldownActionId
			let filtersForDataset = uniqueDrilldownActionId(
				existingFilters.concat(filters))

			filtersByDatasetId[datasetId] = filtersForDataset
		}

		// start with any filters from table drilldowns
		_(this.drilldownFiltersByViewCid).toPairs().forEach(pair => {
			let view = this.widgetViews.concat(this.chartViews).find(v => v.cid == pair[0])
			if (!view) {
				return
			}

			let viewFilters = pair[1]
			let datasetId = view.model.datasetId()

			addFilters(datasetId, viewFilters)
		})

		// add in the current state of any filter widgets
		this.widgetViews.filter(isDatasetFilterView).forEach(view => {
			addFilters(view.model.datasetId(), [ view.filter() ])
		})

		// add in any manually-added drawer filters
		_(this.drawerFilters).toPairs().forEach(([datasetId, drawerFilters]) => {
			addFilters(datasetId, drawerFilters)
		})

		return filtersByDatasetId
	},

	queryAll () {
		// Fire off queries for each dataset on the dashboard
		let datasetIdsFromCharts = _.flatMap(this.chartViews,
				v => v.model.chart.getAllDatasetIds())
		_(this.widgetViews)
			.map(v => parseInt(v.model.datasetId()))
			.concat(datasetIdsFromCharts)
			.uniq()
			.forEach(id => this.query(id))

		// Also fetch any charts that didn't have dataset data
		this.fetchCharts(null)
	},

	query (datasetId, widgetViews, skipViews) {
		if (!_.isNil(widgetViews) && !_.isArray(widgetViews)) {
			widgetViews = [ widgetViews ]
		}

		// #23173 when copy/pasting multiple dataset widgets, we don't want each 'add'
		// event firing off a query. Instead, queue them up
		this.queryQueue.add(datasetId, {
			includeViews: widgetViews || ALL,
			excludeViews: skipViews
		})
	},

	onQueueReady (byDatasetId) {
		_.toPairs(byDatasetId).forEach(([ datasetId, { includeViews, excludeViews } ]) => {
			this._doQuery(datasetId, { includeViews, excludeViews })
		})
	},

	_doQuery (datasetId, { includeViews, excludeViews }) {
		let widgetViews = includeViews === ALL ? null : includeViews

		let widgetViewsForThisDataset = this.widgetViews
			.filter(v => isDatasetValueView(v) || isDatasetTableView(v) || isDatasetFilterView(v))
			.filter(v => {
				// any views for the same dataset are definitely affected
				if (v.model.datasetId() == datasetId) {
					return true
				}

				// #19781 need to include filter widgets on linked datasets
				if (isDatasetFilterView(v)) {
					return filterAppliesToDataset({
						dashboardData: v.model.data,
						filter: v.filter(),
						subjectDatasetId: datasetId
					})
				}

				return false
			})

		// If a widgetView was passed in, that's the only one we want to update
		// (example: a new widget was added)
		let widgetViewsToUpdate = widgetViews ? widgetViews : widgetViewsForThisDataset

		widgetViewsToUpdate.forEach(view => _.invoke(view, 'loading'))

		let requests = this.groupRequests(datasetId, widgetViewsForThisDataset)

		if (this.xhrsByDatasetId[datasetId]) {
			this.xhrsByDatasetId[datasetId].forEach(xhr => xhr.abort())
		}
		this.xhrsByDatasetId[datasetId] = []

		this.queryTables(datasetId, widgetViews, requests.tableGroups)
		this.queryValues(datasetId, widgetViews, requests.valueRequests)
		this.fetchCharts(datasetId, excludeViews)
	},

	queryValues (datasetId, widgetViewsToUpdate, valueRequests) {
		valueRequests = valueRequests.filter(({ view }) => {
			if (widgetViewsToUpdate) {
				// If we only want to update specific widgets, strip out any requests
				// that don't involve them
				return !!widgetViewsToUpdate.find(v => v.cid == view.cid)
			} else {
				return true
			}
		})

		if (valueRequests.length === 0) {
			this.trigger('query:skipped')
			return
		}

		let data = {
			valueRequests: _(valueRequests).map(({ request, view }) => {
				return [ view.cid, request ]
			}).fromPairs().value()
		}

		let xhr = $.ajax({
			type: 'post',
			url: `api/datasets/${datasetId}/query-values`,
			data: JSON.stringify(data),
			success: response => {
				this.trigger('query:done')

				response.forEach(valueResponse => {
					let view = valueRequests
						.map(r => r.view)
						.find(v => v.cid == valueResponse.widgetCid)

					if (valueResponse.error) {
						// #19004 if there was an error calculating the value for
						// this widget, the http request will still be 200, but
						// the error will be in response.error
						view.renderDatasetError(valueResponse.error)
					} else {
						view.renderDatasetResponse(valueResponse)
					}
				})
			},
			error: (response, statusText) => {
				queryErrorCallback(response, statusText, valueRequests.map(r => r.view))
			}
		})

		this.xhrsByDatasetId[datasetId].push(xhr)
	},

	queryTables (datasetId, widgetViews, tableGroups) {
		// Note that we still use **all** the widgets for this dataset when building
		// the requests, because we want any active filters to be used even when
		// only updating a single widget
		let groups = tableGroups.filter(group => {
			if (widgetViews) {
				// If we only want to update specific widgets, strip out any requests
				// that don't involve them
				let groupCids = _.map(group.views, 'cid')
				let specifiedViewCids = _.map(widgetViews, 'cid')
				return _.intersection(groupCids, specifiedViewCids).length > 0
			} else {
				return true
			}
		})

		if (groups.length === 0) {
			this.trigger('query:skipped')
			return
		}

		this.xhrsByDatasetId[datasetId].push(...groups.map(group => {
			return $.ajax({
				type: 'post',
				url: `api/datasets/${datasetId}/query`,
				data: JSON.stringify(group.request),
				success: response => {
					if (response.treeItems) {
						this.data.addDatasetTreeItems(response.treeItems)
						this.addTreeItemsToFilterWidgets(response.treeItems)
					}

					// #30859 make sure each dataset widget's model has the
					// dataset metadata
					if (response.dataset) {
						let dataset = response.dataset
						group.views.forEach(view => {
							if (!view.model.data.datasets(dataset.id)) {
								view.model.data.datasets(dataset.id, dataset)
							}
						})
					}

					this.trigger('query:done')

					group.views.forEach((view, i) => {
						view.renderResult(group.request, response, _.get(response, `results[${i}]`))
					})
				},
				error: (response, statusText) => {
					queryErrorCallback(response, statusText, group.views)
				}
			})
		}))
	},

	groupRequests (datasetId, widgetViewsToQuery) {
		let dataset = this.datasetModel(datasetId)

		// mapLinkGroup will assign all filters passed to it a unique linkGroup, then
		// increment the linkGroup for use the next time mapLinkGroup is called.
		// We start from the number after the highest linkGroup that's already used.
		let maxInitialLinkGroup = _(widgetViewsToQuery)
			.filter(v => {
				if (isDatasetTableView(v) && _.isNil(v.datasetId())) {
					// dataset table widgets can have "None" for the source,
					// in which case there's no query to make
					return false
				}
				return true
			})
			.flatMap(v => {
				if (isDatasetValueView(v) || isDatasetTableView(v)) {
					let filters = v.filters() || []

					let aggregations
					if (isDatasetValueView(v)) {
						aggregations = v.model.aggregations()
					} else if (isDatasetTableView(v)) {
						aggregations = v.table().get('aggregations').flatMap(agg => {
							return _.concat([agg], agg.aggregations)
						})
					}

					let aggFilters = _(aggregations)
						.compact()
						.flatMap(a => a.filters)
						.compact()
						.value()

					return _.concat(filters, aggFilters)
				} else if (isDatasetFilterView(v)) {
					return v.filter()
				}
			})
			.map('linkGroup')
			.max()

		let mapLinkGroup = uniqueLinkGroup(_.isNil(maxInitialLinkGroup)
			? 1
			: (maxInitialLinkGroup + 1))

		// Makes sure that all the manual filters in the given array have appropriate linkGroup
		// IDs that won't collide with filters from other sources
		function adjustManualFilterLinkGroups (filters) {
			// Get any manual filters that were already part of linkGroups, and
			// re-map them using mapLinkGroup so that they won't collide with any
			// shared filter linkGroups.
			let manualFilters = _(filters)
				.filter(filter => !_.isNil(filter.linkGroup))
				.groupBy('linkGroup')
				.toPairs()
				.flatMap(pair => {
					let originalGroupId = pair[0]
					let filtersInGroup = pair[1]

					return mapLinkGroup(filtersInGroup)
				}).value()

			// Now add back in each manual filter **without** an linkGroup.
			// Note that we can leave manual filters without an linkGroup alone,
			// because that is treated the same as a dedicated linkGroup
			return manualFilters.concat(_(filters)
				.filter(filter => _.isNil(filter.linkGroup))
				.value())
		}

		function adjustRequestAggregationLinkGroups (request) {
			// #42621 #49423 need to remap the linkGroups for each aggregation.filters
			_.forEach(request.aggregations, agg => {
				adjustAggregationLinkGroups(agg)
			})
		}

		function adjustAggregationLinkGroups (agg) {
			agg.filters = adjustManualFilterLinkGroups(agg.filters)
			_.forEach(agg.aggregations, subagg => {
				subagg.filters = adjustManualFilterLinkGroups(subagg.filters)
			})
		}

		function adjustTableAggregationLinkGroups (table) {
			_.forEach(table.aggregations, agg => adjustAggregationLinkGroups(agg))
		}

		let filterWidgetFilters = _(this.widgetViews)
			.filter(isDatasetFilterView)
			// #18611 ignore "incomplete" filter widgets (eg 'is-any' filter on a rollup field, but
			// nothing selected) so they aren't considered when deciding whether to show the Back button
			.filter(v => {
				let field = v.field()
				return field ? isComplete(v.filter(), field) : false
			})
			.map(v => {
				let filter = _.cloneDeep(v.filter())

				applyVisibleLinkedFieldId({
					dashboardData: v.model.data,
					filter,
					subjectDatasetId: datasetId
				})

				return filter
			})
			.value()

		// Returns any current filters for this dataset that should be used in
		// a dataset request
		let getSharedFilters = (viewsInRequest) => {
			// Use any drilldown filters that have been created at runtime (example: drilldown on a table)
			let shared = _(this.drilldownFiltersByViewCid)
				.toPairs()
				.filter(([viewCid, filters]) => {
					// #57995 Skip any filter widgets (they are accounted for in
					// filterWidgetFilters)
					let view = this.widgetViews.concat(this.chartViews)
						.find(v => v.cid == viewCid)
					return view && !isDatasetFilterView(view)
				})
				.flatMap(([viewCid, filters]) => {
					return buildApplicableFilters({
						dashboardData: this.data,
						filters,
						subjectDatasetId: datasetId
					})
				})
				.filter(filter => {
					// #56299 ignore "incomplete" shared filters (eg 'is-any'
					// filter on a rollup field, but nothing selected) so they
					// aren't sent to the server (potentially messing up the query)
					let field = dataset.getUnderlyingField(filter.fieldId)
					return isComplete(filter, field)
				})
				.value()

			// Add in any filters manually added from the drawer
			_.chain(dataset).get('datasetsById', {}).keys()
				.forEach(id => {
					shared = shared.concat(buildDrawerFilters({
						dashboardData: this.data,
						drawerFilters: this.drawerFilters[id] || [],
						subjectDatasetId: datasetId
					}))
				}).value()

			let applicableFilterWidgetFilters = getApplicableFilterWidgetFilters({
				filterWidgetFilters,
				viewsInRequest,
				dashboardData: this.data,
			})

			// Add in any active filter widget filters, making sure any duplicate
			// drilldown action filters are removed
			return uniqueDrilldownActionId(shared.concat(applicableFilterWidgetFilters))
		}

		let groups = []
		// we can combine the XHR request for table widgets that have the same date field
		let tableViews = _(widgetViewsToQuery)
			.filter(isDatasetTableView).value()

		var request = {
			id: datasetId,
			calendarPeriodId: this.getPeriodId(),
			showTables: true,
			others: true,
			showDashboardTables: true,
			ignoreDefaultCalendarFieldId: true,
			token: this.embeddedToken
		}

		// Any tables with manual filters have to be done in their own request,
		// because manual filters shouldn't affect other widgets
		tableViews.filter(view => view.model.hasManualFilter()).forEach(view => {
			let tableRequest = _.cloneDeep(request)
			let table = tableForRequest(view).toJSON()
			tableRequest.tables = [ table ]

			// Merge the current table's manual filters with any shared filters
			let manualFilters = adjustManualFilterLinkGroups(view.filters())
			adjustTableAggregationLinkGroups(table)

			// #18610 Some manual filters on dataset tables may have turned into shared filters
			// (eg a table with a rollup tree manual filter, user clicks on a row to drilldown,
			// now that table's manual filter was drilled down and shared).
			//
			// Make sure they aren't duplicated by filtering them out of the
			// shared filters list
			let drilldownActionIds = _(manualFilters)
				.map('drilldownActionId')
				.reject(_.isNil)
				.uniq().value()

			let shared = getSharedFilters([view])
				.filter(f => !_.includes(drilldownActionIds, f.drilldownActionId))

			tableRequest.filters = manualFilters.concat(shared)
			// #19054 indicate that the "total" data isn't required to save
			// some queries, since we are not requesting data for a value widget
			tableRequest.total = false

			// #60721 the filters on the table itself have already been
			// extracted, adjusted (eg. linkGroup adjustments), and added to
			// tableRequest directly, so we need to clear them out of the table
			table.filters = []

			groups.push({
				request: tableRequest,
				views: [ view ]
			})
		})

		// Now build a request group for any tables that don't have manual filters
		let groupedTableViews = tableViews.filter(view => !view.model.hasManualFilter())
		if (groupedTableViews.length > 0) {
			request.tables = groupedTableViews.map(v => tableForRequest(v))
			// only have to worry about any existing "shared filters" (drilldowns, filter widgets)
			request.filters = getSharedFilters(groupedTableViews)

			let group = {
				request: request,
				views: groupedTableViews
			}

			// #19054 indicate that the "total" data isn't required to save
			// some queries, since we are not requesting data for a value widget
			request.total = false

			groups.push(group)
		}

		// Now set up a request for each dataset value widget
		let valueViews = widgetViewsToQuery.filter(isDatasetValueView)
		let valueRequests = valueViews.map(valueView => {
			let request = _.extend({
				calendarPeriodId: this.getPeriodId(),
				ignoreDefaultCalendarFieldId: true,
				token: this.embeddedToken,
				// widget ID included for permissions checking on embedded dashboards;
				// see EmbeddedDashboardHelper.buildSavedDashboardDatasetRequest()
				valueWidgetId: valueView.model.get('wob')?.id
			}, valueView.model.datasetRequest())

			// remap the linkGroup for any of the value widget's manual filters, and combine them with
			// the shared filters
			request.filters = adjustManualFilterLinkGroups(request.filters)
				.concat(getSharedFilters([valueView]))

			adjustRequestAggregationLinkGroups(request)

			// #19054 indicate that the "total" data is required, since we are
			// requesting data for a value widget
			request.total = true
			// #35799 don't do unnecessary query
			request.showNumResults = false

			return {
				request: request,
				view: valueView
			}
		})

		return {
			tableGroups: groups,
			valueRequests
		}
	},

	chartUsesDataset (chartView, datasetId) {
		let chart = chartView.model.chart
		let dataset = this.datasetModel(datasetId)

		function isDatasetOrLinkedDataset (id) {
			if (datasetId == id) {
				return true
			}

			if (!dataset) {
				return false
			}

			return !!dataset.datasetsById[id]
		}

		return !!_.find(chart.getAllDatasetIds(), usedDatasetId => {
			return isDatasetOrLinkedDataset(usedDatasetId)
		})
	},

	/**
	 * Returns the Dataset models of any datasets used in the dashboard (including
	 * dataset widgets and charts)
	 */
	datasets: function () {
		let idsFromDatasetWidgets = this.widgetViews
			.map(v => v.model.datasetId())

		let idsFromCharts = _(this.chartViews)
			.flatMap(v => {
				return _.get(v.model.chart.startingDataset(), 'id')
			})
			.filter(id => !_.isNil(id))
			.value()

		let idsFromReports = _(this.datasetReportViews)
			.map(v => {
				let r = v.model.report
				if (!r) {
					return null
				}

				return _.get(r.get('dataset'), 'id')
			})
			.compact()
			.value()

		return _(idsFromDatasetWidgets.concat(idsFromCharts).concat(idsFromReports))
			.map(id => parseInt(id))
			.uniq()
			.map(id => this.datasetModel(id))
			.filter(dataset => !_.isNil(dataset))
			.value()
	},

	datasetModel: function (datasetId) {
		let ds = this.data.datasetModel(datasetId)

		if (!ds) {
			// If the dataset wasn't in the "main" DashboardData, it could be in
			// a chart's ChartData
			ds = _(this.chartViews)
				.map(v => {
					return v.model.chart.data.datasetModel(datasetId)
				})
				.filter(ds => !!ds)
				.first()
		}

		if (ds) {
			return ds
		}

		// The dataset could be in the linkedDatasets of another dataset that
		// is in the request
		for (let parentDataset of _.values(this.data.datasets())) {
			if (_.find(parentDataset.linkedDatasets, l => l.id == datasetId)) {
				return this.datasetModel(parentDataset.id).datasetsById[datasetId]
			}
		}
	},

	drilldownIdsByFieldId: function () {
		let drilldownIds = {}
		// Look at all dataset drilldown capable widgets (currently charts
		// and dataset tables) and get their current drilldown state
		let drilldownableDatasetWidgetFields = this.widgetViews.filter(v => {
			return isDatasetTableView(v) || isDatasetFilterView(v)
		})
		_(this.chartViews).concat(drilldownableDatasetWidgetFields)
			.map(v => {
				return { view: v, fieldAndId: v.drilldownFieldAndId() }
			})
			.filter(({ fieldAndId }) => fieldAndId && fieldAndId.drilldownId)
			.forEach(({ view, fieldAndId }) => {
				// if the drilldown does not have a field id, it means we are
				// viewing sample data. in this event, add a placeholder id to
				// match CHART_PLACEHOLDER_DATASET_FIELD_ID defined in
				// ChartPlaceholderData.kt
				const placeholderDataFieldId = -1

				const fieldId = fieldAndId.fieldId || placeholderDataFieldId
				// There could be multiple widgets using the same dataset field,
				// so give preference to the drilldown state of whatever widget
				// last had a drilldown click
				if (_.isNil(drilldownIds[fieldId]) || view == this.lastDrilldownView) {
					drilldownIds[fieldId] = fieldAndId.drilldownId
				}
			})

		return drilldownIds
	},

	chartTreeItemsById: function() {
		let treeItemsById = {}
		const chartTreeItems = _.map(this.chartViews, v => v.model.chart.data.treeItems())
		_.forEach(chartTreeItems, treeItems => {
			treeItemsById = _.extend(treeItemsById, treeItems)
		})

		return treeItemsById
	},

	addTreeItemsToFilterWidgets (treeItems = []) {
		// For when the filter widget's filter was changed by something else
		// (like a map drilldown), this makes sure the filter widget has a
		// reference to the new drilldown tree item and re-renders it
		this.widgetViews.filter(isDatasetFilterView).forEach(v => {
			v.model.data.addDatasetTreeItems(treeItems)
			v.render()
		})
	},

	remove: function () {
		this.stopListening()
		this.removed = true
	}
})

/**
 * Offsets the linkgroup of live filters so they don't clash with
 * request.filters (from the value widget) and removes deletable flags
 */
function adjustLiveFiltersForDrilldown(liveFilters, request) {
	const maxLinkGroup = _(request.filters).map('linkGroup').max() ?? 0
	liveFilters.forEach(f => {
		// #26700 remove any "deletable: false" flags from filter widget filters
		// so that they are deletable on the dataset overview
		delete f.deletable

		// #57546 ensure that the linkGroups on the request's filters and
		// the live filters don't clash
		if (f.linkGroup) f.linkGroup += maxLinkGroup
	})
}

function isType (type, view) {
	return view.model.properties.get('type') === type
}

const isDatasetFilterView = isType.bind(null, 'dataset-filter')
const isDatasetTableView = isType.bind(null, 'dataset-table')

/**
 * Returns true if this is a widget view that renders a dataset query value
 * (like a shape or speedometer). This does *not* include widgets like
 * dataset tables or charts
 */
function isDatasetValueView (view) {
	if (isPotentialDatasetValueModel(view.model)) {
		return view.model.isDatasetSource()
	}

	return false
}

function isChartView (view) {
	return isChartModel(view.model)
}

function isChartModel (model) {
	return model.properties.get('type') === 'chart'
}

function isChartTableView (view) {
	return isChartTableModel(view.model)
}

function isChartTableModel (model) {
	return model.properties.get('type') === 'chart-table'
}

function isDatasetReportModel (model) {
	return model.properties.get('type') === 'report' &&
		model.properties.get('reportSubtype') === 'dataset-report-designer'
}

function isDatasetChartView (view) {
	if (!isChartView(view)) {
		return false
	}

	return view.model.chart.hasDatasetSources()
}

function isDrilldownChartView (view) {
	if (!isChartView(view)) {
		return false
	}

	return view.model.chart.isHierarchical()
}

function isDatasetWidgetModel (widgetModel)  {
	let type = unwrapModel(widgetModel).properties.type
	if (_.includes(['dataset-table', 'dataset-filter'], type)) {
		return true
	}

	if (isPotentialDatasetValueModel(widgetModel) && widgetModel.isDatasetSource()) {
		return true
	}

	return false
}

/**
 * Returns true if this is a model for a widget that *could* render a dataset
 * query value (like a shape or speedometer). This does *not* include widgets
 * like dataset tables or charts, and it does not account for whether the widget
 * actually *is* using a dataset source. For example, a shape widget with a
 * scorecard source would return true, because it could later be changed to
 * a dataset source.
 */
function isPotentialDatasetValueModel (widgetModel) {
	return _.includes(['shape', 'speedometer', 'linear-gauge'],
		unwrapModel(widgetModel).properties.type)
}

/**
 * Returns a function that will clone the given filters and assign them the next
 * linkGroup ID. Useful for ensuring that linkGroups from different sources don't
 * have ID collisions
 */
function uniqueLinkGroup (start = 0) {
	let linkGroup = start
	return function (filters) {
		filters = _.isArray(filters) ? filters : [filters]

		let currentLinkGroup = linkGroup++

		let clones = _.cloneDeep(filters)
		return _.forEach(clones, clone => clone.linkGroup = currentLinkGroup)
	}
}

/**
 * Given the drawer filters for a particular dataset, returns the copies of
 * the filters that should be applied to the subjectDataset. If necessary,
 * alterations may be made (like adding a visibleLinkedFieldId)
 *
 * @param {DashboardData} dashboardData
 * @param {ApiDatasetFilter[]} drawerFilters
 * @param {Integer} subjectDatasetId
 *
 * @return {ApiDatasetFilter[]}
 */
function buildDrawerFilters ({ dashboardData, drawerFilters, subjectDatasetId }) {
	let [ editFieldFilters, otherFilters ] = _.partition(drawerFilters, f => {
		return isEditField(f.fieldId)
	})

	let applicableOtherFilters = buildApplicableFilters({
		dashboardData,
		filters: otherFilters,
		subjectDatasetId
	})

	// We can include all the edit field filters that were passed in, because
	// `drawerFilters` only includes filters from the drawer that were
	// specifically added for this dataset (or one of its linked datasets)
	return editFieldFilters.concat(applicableOtherFilters)
}

/**
 * Given an array of filters, returns the filters that apply to the given subject
 * dataset. Filters are cloned, and may be altered (like adding a visibleLinkedFieldId)
 */
function buildApplicableFilters ({ dashboardData, filters, subjectDatasetId }) {
	let applicableFilters = []

	filters.forEach(f => {
		let filter = _.cloneDeep(f)

		if (filterAppliesToDataset({
			dashboardData,
			filter,
			subjectDatasetId
		})) {
			applyVisibleLinkedFieldId({
				dashboardData,
				filter,
				subjectDatasetId
			})

			applicableFilters.push(filter)
		}
	})

	return applicableFilters
}

/**
 * Adds the necessary visibleLinkedFieldId to the given filter if there is a
 * single link between the subject dataset and the filter's dataset. If there is
 * more than one link, it is ambiguous which one should be used, so the filter
 * will be left alone. See https://github.com/SpiderStrategies/Scoreboard/issues/27000#issuecomment-551239495
 */
function applyVisibleLinkedFieldId ({ dashboardData, filter, subjectDatasetId }) {
	// Note: this logic is basically duplicated server-side in DatasetFilterAdjuster.java.
	// If the logic here changes, change it there, too.
	let info = filterApplicablityInfo.apply(null, arguments)

	if (info.visibleLinkedFieldId) {
		filter.visibleLinkedFieldId = info.visibleLinkedFieldId
	}
}

/**
 * Returns true if the given filter can be applied to the given dataset. See
 * applyVisibleLinkedFieldId() for details.
 */
function filterAppliesToDataset ({
	dashboardData, filter, subjectDatasetId, dataset
}) {
	return filterApplicablityInfo.apply(null, arguments).applicable
}

function filterApplicablityInfo ({
	dashboardData, filter, subjectDatasetId, dataset
}) {
	// Note: this logic is basically duplicated server-side in FilterApplicabilityChecker.kt
	// If the logic here changes, change it there, too.

	let subjectDataset = dataset || dashboardData.datasetModel(subjectDatasetId)
	subjectDatasetId = subjectDatasetId || unwrapModel(dataset).id
	if (!subjectDataset) {
		return { applicable: false }
	}

	let filterField = subjectDataset.combinedFieldsById[filter.fieldId]

	if (!filterField) {
		return { applicable: false }
	}

	// If we reached this point, it's either a native field or a linked
	// field. We used to only allow filters on linked fields if there
	// was a unique path to the dataset (#26982), but that was causing
	// problems (#60539), so now we allow it.
	return { applicable: true }
}

function getDrilldownActionId (filters) {
	return _(filters).map(f => f.drilldownActionId).compact().first()
}

function tableForRequest (tableView) {
	let table = tableView.table()
	// widget ID included for permissions checking on embedded dashboards;
	// see EmbeddedDashboardHelper.buildSavedDashboardDatasetRequest()
	table.set('tableWidgetId', tableView.model.get('wob')?.id)
	return table
}

function queryErrorCallback (response, statusText, views) {
	if (statusText === 'abort') {
		// Request was aborted, most likely because another request was made afterwards.
		// Just don't process it
		return
	}

	if (_.get(response, 'responseJSON.messageCode')) {
		views.forEach(view => {
			// #58572
			// Some of the views passed in may not have a renderDatasetError
			// method, so we need to check before calling it
			view?.renderDatasetError?.(response.responseJSON)
		})
		return
	}

	appDisplay.dialog(new ErrorView(response.responseJSON))
}

const DATASET_FETCH_PROPERTIES = ['aggregationType', 'calendarFieldId', 'fieldId', 'filter', 'filters']

/**
 * Extracts the DrilldownState for a given filter. This is useful when you
 * have a filter that you want to apply as if a drilldown action was performed,
 * like when a filter widget on a rollup field is edited
 *
 * @param {ApiDatasetFilter} filter
 *
 * @return {DrilldownState}
 */
function getDrilldownState (filter) {
	// Note: drilldownId could be null, and that's okay; the filters will
	// be incomplete (and thus ignored), and the tables/map widgets will
	// be at the root level
	let drilldownId = null
	if (['is', 'is-any'].includes(filter.operator)) {
		// Only want to look at rollupIsAnyFilters if the operator actually
		// uses it
		drilldownId = _.keys(filter.rollupIsAnyFilters)[0]
	}

	return {
		drilldownId,
		drilldownFilters: [ filter ],
		fieldId: filter.fieldId
	}
}

/**
 * For a given set of widget views, returns the filters that should be included
 * in their dataset query request.
 *
 * @param {ApiDatasetFilter[]} filterWidgetFilters - the filters from all
 * filter widgets on the dashboard
 *
 * @param {WidgetView[]} viewsInRequest - the widget views that are part of the
 * dataset query request
 *
 * @param {DashboardData} dashboardData
 *
 * @return {ApiDatasetFilter[]} - the filters that should be included in the
 * datset query request
 */
function getApplicableFilterWidgetFilters ({ filterWidgetFilters, viewsInRequest, dashboardData }) {
	let datasetIdsInRequest = _(viewsInRequest)
		.map(v => {
			if (isDatasetTableView(v)) {
				return [v.datasetId()]
			} else if (isDatasetValueView(v)) {
				return v.model.datasetIds()
			}
		})
		.flatMap()
		.compact()
		.value()

	return filterWidgetFilters.filter(f => {
		return _.some(datasetIdsInRequest, datasetId => {
			return filterAppliesToDataset({
				dashboardData: dashboardData,
				filter: f,
				subjectDatasetId: datasetId
			})
		})
	})
}

/**
 * For a given datasetId that needs to be fetched, returns all other datasetIds
 * that should also be fetched.
 * @param {Dataset} dataset
 * @param {WidgetView[]} widgetViews
 * @return {Int[]}
 */
function getDatasetIdsToFetch ({ dataset, widgetViews }) {
	let datasetIdsToFetch = new Set()

	let allRelatedDatasets = _.keys(dataset.datasetsById)
	allRelatedDatasets.forEach(relatedDatasetId => {
		// #19781 a drilldown or filter on a given dataset filter can also affect
		// widgets/charts pointing to a linked dataset, so we'll need to fetch
		// for them all
		datasetIdsToFetch.add(parseInt(relatedDatasetId))

		// #60284 We also need to find any calculated value widgets that
		// reference the related dataset, and then include all datasets that
		// are referenced in the equation (which might not be linked to the
		// relatedDataset)
		widgetViews
			.filter(isDatasetValueView)
			.forEach(v => {
				let datasetIdsInEquation = v.model.datasetIds()
				if (_.find(datasetIdsInEquation, eqId => eqId == relatedDatasetId)) {
					datasetIdsInEquation.forEach(id => datasetIdsToFetch.add(id))
				}
			})
	})

	return Array.from(datasetIdsToFetch)
}

export default Maestro
export { DATASET_FETCH_PROPERTIES }
export { isDatasetWidgetModel, isChartModel, filterAppliesToDataset,
	adjustLiveFiltersForDrilldown, getDrilldownState, buildDrawerFilters,
	getApplicableFilterWidgetFilters, getDatasetIdsToFetch }

Maestro.__filename__ = 'dashboards/widgets/maestro.js'
